import React, {useEffect, useMemo, useState} from "react";
import ShippingForm, {IShippingFormData} from "./ShippingForm";
import {useTranslation} from "react-i18next";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import Spinner from "../../../utils/loader/Spinner";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {CheckoutError, CheckoutStep} from "../../Checkout";
import window from "../../../lib/WindowExtended";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import {Payments} from "../../../lib/payments/PaymentController";
import genericStyles from "../../../lib/GenericStyles.module.scss";
import styles from "./ShippingForm.module.scss";
import {useForm} from "react-hook-form";
import {submitShippingData} from "./submitShippingData";

const Shipping: React.FC<{isVisible: boolean}> = ({isVisible}) => {
  const {t} = useTranslation(["errorcodes", "translations"]);
  const {gotoStep, shippingOptions} = useCheckoutViewContext();
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const {checkout} = useCheckoutContext();
  const {deliveryAddress} = checkout;

  const filteredShippingOptions = shippingOptions.filter((option) => {
    // - If street is 'Postfiliale'/'Packstation', only return the allowed ones
    // - If any express payment is chosen (and step controller says, we need a
    //   phone number for the logistics type chosen inside the payment modal),
    //   stop here for phoneNo input, and only show that particular logistics type
    return (
      !(deliveryAddress?.street === "Postfiliale" && !option.isPostOfficeAllowed) &&
      !(deliveryAddress?.street === "Packstation" && !option.isPackingStationAllowed) &&
      (checkout.paymentId !== Payments.PaypalExpressBraintree || option.logisticsTypeId === checkout.logisticsTypeId) &&
      (checkout.paymentId !== Payments.GooglePay || option.logisticsTypeId === checkout.logisticsTypeId) &&
      (checkout.paymentId !== Payments.ApplePay || option.logisticsTypeId === checkout.logisticsTypeId)
    );
  });

  const defaultLogisticsType = useMemo(
    () =>
      filteredShippingOptions.find((shipping) => shipping.logisticsTypeId === checkout.logisticsTypeId) ||
      filteredShippingOptions.find((shipping) => !!shipping.isCustomerPreferredShipping) ||
      filteredShippingOptions.find((shipping) => shipping.isBestMatch) ||
      filteredShippingOptions[0],
    [filteredShippingOptions],
  );

  /**
   * react-hook-form init
   */
  const methods = useForm<IShippingFormData>({
    defaultValues: {
      shippingOption: defaultLogisticsType,
      phone: checkout.deliveryAddress?.phone || "",
      shippingDate: checkout.favouredDateShipping,
      isPartDeliveryAllowed: checkout.isPartDeliveryAllowed,
    },
  });

  useEffect(() => {
    if (isVisible) {
      const {logisticsTypeId} = methods.watch("shippingOption");
      if (logisticsTypeId !== checkout.logisticsTypeId) {
        setIsLoading(true);
        methods.clearErrors();

        void checkout.put({logisticsTypeId}).then(() => {
          gotoStep(CheckoutStep.SHIPPING);
          setIsLoading(false);
        });
      }
    } else {
      setIsLoading(false);
      setGeneralErrors([]);
      window.isIntoSettingPhoneNoInShippingStep = false;
    }
  }, [isVisible, methods.watch("shippingOption")]);

  useEffect(() => {
    const messages = checkout.deliveryAddress?.messages || [];
    const phoneRelevantMessage = messages?.find((msg) => msg.type === "error" && msg.field === "phone")?.code;
    if (phoneRelevantMessage) {
      methods.setError("phone", {type: "validate", message: t(phoneRelevantMessage)});
    }
  }, [checkout.deliveryAddress?.messages]);

  useEffect(() => {
    if (methods.watch("phone")) {
      methods.clearErrors();
    }
  }, [methods.watch("phone")]);

  if (!isVisible) {
    return null;
  }

  if (!filteredShippingOptions.length) {
    return (
      <div className={genericStyles.mobileOnlyFriendly}>
        <p>
          {t("checkout.noShippingOptions")}
          <span> {" " + t("form.checkAddress")}</span>
        </p>
      </div>
    );
  }

  const onSubmit = methods.handleSubmit(async () => {
    setIsLoading(true);
    setGeneralErrors([]);
    const submitReturn = await submitShippingData(methods.watch(), checkout);
    setIsLoading(false);
    if (submitReturn) {
      if (submitReturn.generalErrors) setGeneralErrors(submitReturn.generalErrors);
      if (submitReturn.phoneError) methods.setError("phone", {type: "validate", message: t(submitReturn.phoneError)});
      if (submitReturn.canGoTo) gotoStep();
    }
  });

  return (
    <div className={`Shipping ${genericStyles.checkoutContainer} ${genericStyles.mobileOnlyFriendly}`}>
      <h2>{t("checkout.shippingOptions")}</h2>
      <CheckoutError>{generalErrors}</CheckoutError>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form className={styles.shippingForm} onSubmit={onSubmit}>
        <ShippingForm filteredShippingOptions={filteredShippingOptions} methods={methods} />
        <CartDetails />
        <Button styles={[ButtonStyles.wide]} type={isLoading ? ButtonType.disabled : ButtonType.default}>
          {isLoading ? <Spinner /> : <span>{t("general.continue")}</span>}
        </Button>
      </form>
    </div>
  );
};

export default Shipping;
